import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../../../../lib/supabaseClient";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDownIcon } from "lucide-react";
import { getTournamentTheme } from "../../../../../styles/themes";

const formatRelativeToPar = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "–";
    if (number === 0) return "E";
    return number > 0 ? `+${number}` : String(number);
};

const getHoleResultLabel = (strokes, par) => {
    if (strokes == null || par == null) return "Ej spelat";

    const difference = Number(strokes) - Number(par);
    if (difference <= -3) return "Albatross eller bättre";
    if (difference === -2) return "Eagle";
    if (difference === -1) return "Birdie";
    if (difference === 0) return "Par";
    if (difference === 1) return "Bogey";
    return "Dubbelbogey eller sämre";
};

const getHoleScoreMarkerClass = (strokes, par) => {
    if (strokes == null || par == null) return "text-current opacity-45";

    const difference = Number(strokes) - Number(par);
    if (difference <= -2) return "rounded-full ring-2 ring-[#d6ad35] text-[#7a5a0a]";
    if (difference === -1) return "rounded-full ring-2 ring-[#bd4d49] text-[#9f3532]";
    if (difference === 1) return "rounded-[4px] bg-[#dcecf5] text-[#24465d]";
    if (difference >= 2) return "rounded-[4px] bg-[#315b79] text-white";
    return "text-current";
};


const getPlayerIdsKey = (playerIds = []) =>
    [...playerIds]
        .map((playerId) => String(playerId))
        .sort((a, b) => a.localeCompare(b, "sv-SE"))
        .join("|");

export default function RoundInfo({
    activeRoundIndex,
    setActiveRoundIndex,
    rounds,
    players,
    activeRound,
    tournament,
    setTournament,
    expandPlayerRequest = null,
}) {
    const [teamScores, setTeamScores] = useState([]);
    const [roundScorecardData, setRoundScorecardData] = useState({
        loading: false,
        liveRound: null,
        units: [],
        scores: [],
    });
    const [expandedScorecardId, setExpandedScorecardId] = useState(null);
    const scorecardRowRefs = useRef({});
    const handledExpansionRequestRef = useRef(null);
    const theme = getTournamentTheme(tournament?.theme_key);

    const mode = activeRound.modes?.[0] || "Unknown";
    const isScramble = mode.toLowerCase().includes("scramble");
    const pairingIsPosition = activeRound.scrambleOptions?.pairing === "position";
    const [showInfo, setShowInfo] = useState(false);
    const [teamHandicaps, setTeamHandicaps] = useState({});
    const isHandicapAdjusted = mode.toLowerCase().includes("handicap");
    const isScrambleWithHandicap = isScramble && activeRound.scrambleOptions?.scrambleWithHandicap;
    const isIndividualWithHandicap = !isScramble && isHandicapAdjusted;
    const [hasManuallyChangedRound, setHasManuallyChangedRound] = useState(false);


    useEffect(() => {
        if (!players || players.length === 0 || !rounds || rounds.length === 0) return;
        if (hasManuallyChangedRound) return; // ⛔️ Hoppa om användaren själv bläddrat

        let targetIndex = 0;

        for (let i = 0; i < rounds.length; i++) {
            const hasScores = players.some(
                (p) => typeof p.scores?.[i] === "number" && p.scores[i] > 0
            );
            if (hasScores) targetIndex = i + 1;
        }

        if (targetIndex >= rounds.length) {
            targetIndex = rounds.length - 1;
        }

        setActiveRoundIndex(targetIndex);
    }, [players, rounds, hasManuallyChangedRound, setActiveRoundIndex]);


    const generatedTeams = useMemo(() => {
        const pairingType = activeRound.scrambleOptions?.pairing || "position";
        const customPairs = activeRound.scrambleOptions?.customTeams || {};

        // 🟡 1. CUSTOM pairing – bygg teams direkt från customTeams
        if (pairingType === "custom" && Object.keys(customPairs).length > 0) {
            const used = new Set();
            const teams = [];

            for (const [p1, p2] of Object.entries(customPairs)) {
                if (!used.has(p1) && !used.has(p2)) {
                    teams.push([p1, p2]);
                    used.add(p1);
                    used.add(p2);
                }
            }

            return teams;
        }

        // 🟢 2. POSITION pairing – returnera befintliga teams om de finns
        if (pairingType === "position") {
            if (!isScramble || activeRoundIndex === 0) {
                return activeRound.teams || [];
            }
        }

        // 🔵 3. SCORE-based pairing (default)
        const thisRoundHasScores = players.some(
            (p) => typeof p.scores?.[activeRoundIndex] === "number" && p.scores[activeRoundIndex] > 0
        );

        if (thisRoundHasScores && activeRound.teams?.length > 0) {
            return activeRound.teams;
        }

        const hasTeamAssignments = tournament.team_data?.assignments &&
            Object.keys(tournament.team_data.assignments).length > 0;

        const pairBy = (group, key = "total") => {
            const sorted = [...group].sort((a, b) => {
                const aVal = a[key] ?? Infinity;
                const bVal = b[key] ?? Infinity;
                return aVal - bVal;
            });

            const teams = [];
            let left = 0;
            let right = sorted.length - 1;

            while (left < right) {
                teams.push([sorted[left++].id, sorted[right--].id]);
            }

            if (left === right) {
                teams.push([sorted[left].id]); // ensam spelare
            }

            return teams;
        };


        let grouped = [];

        if (hasTeamAssignments) {
            const teamAssignments = tournament.team_data.assignments;
            const red = [];
            const green = [];

            players.forEach((p) => {
                const playerData = {
                    ...p,
                    total: p.scores.slice(0, activeRoundIndex).reduce((sum, s) => sum + (s || 0), 0),
                };

                const team = teamAssignments[p.id];
                if (team === "red") red.push(playerData);
                else if (team === "green") green.push(playerData);
            });

            grouped = [
                ...pairBy(red, pairingType === "handicap" ? "handicap" : "total"),
                ...pairBy(green, pairingType === "handicap" ? "handicap" : "total"),
            ];
        } else {
            const withData = players.map((p) => ({
                ...p,
                total: p.scores.slice(0, activeRoundIndex).reduce((sum, s) => sum + (s || 0), 0),
                handicap: typeof p.handicap === "number" ? p.handicap : 999, // fallback
            }));

            grouped = pairBy(withData, pairingType === "handicap" ? "handicap" : "total");
        }

        return grouped;
    }, [
        isScramble,
        activeRoundIndex,
        players,
        activeRound.teams,
        tournament.team_data,
        activeRound.scrambleOptions,
    ]);



    useEffect(() => {
        if (!isScramble) return;

        const existing = activeRound.teams || [];
        const isDifferent = JSON.stringify(existing) !== JSON.stringify(generatedTeams);

        const thisRoundHasScores = players.some(
            (p) => typeof p.scores?.[activeRoundIndex] === "number" && p.scores[activeRoundIndex] > 0
        );

        if (isDifferent && !thisRoundHasScores) {
            // 1. Update local state
            const updatedTournament = { ...tournament };
            updatedTournament.rounds[activeRoundIndex].teams = generatedTeams;
            setTournament(updatedTournament);

            // 2. Update in Supabase (save to rounds table)
            const currentRound = tournament.rounds[activeRoundIndex];
            supabase
                .from("rounds")
                .update({ teams: generatedTeams })
                .eq("id", currentRound.id)
                .then(({ error }) => {
                    if (error) {
                        console.error("❌ Failed to save generated teams to Supabase", error);
                    }
                });
        }
    }, [
        generatedTeams,
        activeRound.teams,
        isScramble,
        pairingIsPosition,
        activeRoundIndex,
        players,
    ]);

    // Compute team scores
    useEffect(() => {
        if (!isScramble || generatedTeams.length === 0) return;

        const scores = [];
        const handicaps = {};

        for (const team of generatedTeams) {
            const [id1, id2] = team;
            const p1 = tournament.playerData.find((p) => p.id === id1);
            const p2 = tournament.playerData.find((p) => p.id === id2);

            // Score för tabellen
            const score = p1?.scores?.[activeRoundIndex] ?? 0;
            scores.push({ team, score });

            // Handicap-beräkning
            if (activeRound.scrambleOptions?.scrambleWithHandicap && p1 && p2) {
                const low = Math.min(p1.handicap, p2.handicap);
                const high = Math.max(p1.handicap, p2.handicap);

                const pctLow = activeRound.scrambleOptions.lowPct ?? 0;
                const pctHigh = activeRound.scrambleOptions.highPct ?? 0;

                const teamHcp = Math.round(low * (pctLow / 100) + high * (pctHigh / 100));
                handicaps[`${id1}-${id2}`] = teamHcp;
            }
        }

        setTeamScores(scores);
        setTeamHandicaps(handicaps);
    }, [generatedTeams, players, activeRoundIndex, isScramble, activeRound.scrambleOptions]);


    useEffect(() => {
        let cancelled = false;
        let channel = null;
        let refreshTimer = null;

        const applyRealtimeScore = (payload) => {
            setRoundScorecardData((current) => {
                if (payload.eventType === "DELETE") {
                    return {
                        ...current,
                        scores: current.scores.filter(
                            (score) => score.id !== payload.old.id
                        ),
                    };
                }

                const nextScore = payload.new;
                const existingIndex = current.scores.findIndex(
                    (score) =>
                        score.id === nextScore.id ||
                        (
                            score.scoring_unit_id === nextScore.scoring_unit_id &&
                            Number(score.hole_number) === Number(nextScore.hole_number)
                        )
                );

                return {
                    ...current,
                    scores:
                        existingIndex === -1
                            ? [...current.scores, nextScore]
                            : current.scores.map((score, index) =>
                                index === existingIndex ? nextScore : score
                            ),
                };
            });
        };

        const loadRoundScorecards = async ({ showLoading = false } = {}) => {
            if (!activeRound?.id) {
                setRoundScorecardData({
                    loading: false,
                    liveRound: null,
                    units: [],
                    scores: [],
                });
                return;
            }

            if (showLoading) {
                setRoundScorecardData({
                    loading: true,
                    liveRound: null,
                    units: [],
                    scores: [],
                });
                setExpandedScorecardId(null);
            }

            try {
                const { data, error } = await supabase.rpc(
                    "get_public_round_scorecard",
                    { p_round_id: activeRound.id }
                );

                if (error) throw error;
                if (cancelled) return;

                const liveRound = data?.liveRound || null;

                setRoundScorecardData({
                    loading: false,
                    liveRound,
                    units: Array.isArray(data?.units) ? data.units : [],
                    scores: Array.isArray(data?.scores) ? data.scores : [],
                });

                // Inloggade administratörer och scorekortsanvändare kan fortfarande
                // få omedelbara Realtime-uppdateringar. För vanliga publika besökare
                // fungerar den återkommande hämtningen nedan som säker reserv.
                if (liveRound?.id && !channel) {
                    channel = supabase
                        .channel(`round-info-scorecards-${liveRound.id}`)
                        .on(
                            "postgres_changes",
                            {
                                event: "*",
                                schema: "public",
                                table: "live_hole_scores",
                                filter: `live_round_id=eq.${liveRound.id}`,
                            },
                            applyRealtimeScore
                        )
                        .subscribe();
                }
            } catch (error) {
                console.error("Kunde inte läsa hål-för-hål-resultat:", error);
                if (!cancelled) {
                    setRoundScorecardData((current) => ({
                        ...current,
                        loading: false,
                    }));
                }
            }
        };

        loadRoundScorecards({ showLoading: true });

        refreshTimer = window.setInterval(() => {
            if (document.visibilityState === "visible") {
                loadRoundScorecards();
            }
        }, 12000);

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                loadRoundScorecards();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", handleVisibilityChange);

            if (refreshTimer) {
                window.clearInterval(refreshTimer);
            }

            if (channel) {
                supabase.removeChannel(channel);
            }
        };
    }, [activeRound?.id]);

    const roundScorecardPars = useMemo(() => {
        const holeCount = Number(
            roundScorecardData.liveRound?.holes || activeRound?.holes || 18
        );
        const source = Array.isArray(roundScorecardData.liveRound?.hole_pars)
            ? roundScorecardData.liveRound.hole_pars
            : Array.isArray(activeRound?.hole_pars)
                ? activeRound.hole_pars
                : [];
        const values = source.map((value) => Number(value));

        return values.length === holeCount && values.every(Number.isFinite)
            ? values
            : null;
    }, [activeRound?.hole_pars, activeRound?.holes, roundScorecardData.liveRound]);

    const roundScorecardEntries = useMemo(() => {
        const holeCount = Number(
            roundScorecardData.liveRound?.holes || activeRound?.holes || 18
        );
        const scoreMap = Object.fromEntries(
            roundScorecardData.scores.map((score) => [
                `${score.scoring_unit_id}-${score.hole_number}`,
                Number(score.strokes),
            ])
        );

        return roundScorecardData.units.map((unit) => {
            const holeScores = Array.from(
                { length: holeCount },
                (_, index) => scoreMap[`${unit.id}-${index + 1}`] || null
            );
            const holesPlayed = holeScores.filter((score) => score != null).length;
            const gross = holeScores.reduce(
                (sum, score) => sum + Number(score || 0),
                0
            );
            const parPlayed = roundScorecardPars
                ? holeScores.reduce(
                    (sum, score, index) =>
                        sum + (score == null ? 0 : Number(roundScorecardPars[index])),
                    0
                )
                : null;
            const relativeToPar =
                roundScorecardPars && holesPlayed > 0
                    ? gross - parPlayed
                    : null;
            const playerIds = unit.player_ids || [];

            return {
                id: unit.id,
                unitId: unit.id,
                unitType: unit.unit_type,
                label:
                    unit.unit_type === "player"
                        ? unit.player_names?.[playerIds[0]] || unit.label
                        : unit.label,
                playerIds,
                playerIdsKey: getPlayerIdsKey(playerIds),
                holeScores,
                holesPlayed,
                gross,
                parPlayed,
                relativeToPar,
                hasScorecardData: true,
            };
        });
    }, [
        activeRound?.holes,
        roundScorecardData.liveRound?.holes,
        roundScorecardData.scores,
        roundScorecardData.units,
        roundScorecardPars,
    ]);

    const roundScorecardByPlayerId = useMemo(() => {
        const map = {};

        roundScorecardEntries.forEach((entry) => {
            if (entry.unitType !== "player") return;
            entry.playerIds.forEach((playerId) => {
                map[String(playerId)] = entry;
            });
        });

        return map;
    }, [roundScorecardEntries]);

    const roundScorecardByTeamKey = useMemo(() => {
        const map = {};

        roundScorecardEntries.forEach((entry) => {
            if (entry.unitType !== "team" || !entry.playerIdsKey) return;
            map[entry.playerIdsKey] = entry;
        });

        return map;
    }, [roundScorecardEntries]);

    // En scramble-rad ska kunna expanderas även när det saknas en exakt
    // matchande live_scoring_unit. Mini game-vinsterna tillhör spelarna
    // och ska därför inte vara beroende av att scorekortsmappningen lyckas.
    const getScrambleExpansionEntry = (team = []) => {
        const teamKey = getPlayerIdsKey(team);
        const teamIdSet = new Set(team.map((playerId) => String(playerId)));

        const liveEntry =
            roundScorecardByTeamKey[teamKey] ||
            roundScorecardEntries.find(
                (entry) =>
                    entry.unitType === "team" &&
                    entry.playerIds.length === team.length &&
                    entry.playerIds.every((playerId) =>
                        teamIdSet.has(String(playerId))
                    )
            );

        const holeCount = Number(
            roundScorecardData.liveRound?.holes || activeRound?.holes || 18
        );
        const teamNames = team
            .map(
                (playerId) =>
                    (tournament.playerData || players || []).find(
                        (player) => String(player.id) === String(playerId)
                    )?.name || "Spelare"
            )
            .join(" & ");

        return {
            ...(liveEntry || {}),
            id: liveEntry?.id || `scramble-${teamKey}`,
            unitId: liveEntry?.unitId || null,
            unitType: "team",
            label: liveEntry?.label || teamNames,
            // Använd alltid lagindelningens spelare här. Då visas rätt person
            // som vinnare även om live-enhetens player_ids är ofullständiga.
            playerIds: [...team],
            playerIdsKey: teamKey,
            holeScores:
                liveEntry?.holeScores ||
                Array.from({ length: holeCount }, () => null),
            holesPlayed: liveEntry?.holesPlayed || 0,
            gross: liveEntry?.gross || 0,
            parPlayed: liveEntry?.parPlayed ?? null,
            relativeToPar: liveEntry?.relativeToPar ?? null,
            hasScorecardData: Boolean(liveEntry),
        };
    };

    useEffect(() => {
        const requestId = expandPlayerRequest?.requestId;
        const requestedPlayerId = expandPlayerRequest?.playerId;
        const requestedRoundIndex = expandPlayerRequest?.roundIndex;

        if (
            requestId == null ||
            requestedPlayerId == null ||
            requestedRoundIndex !== activeRoundIndex ||
            handledExpansionRequestRef.current === requestId
        ) {
            return;
        }

        let scorecardEntry = null;

        if (isScramble) {
            const requestedTeam = generatedTeams.find((team) =>
                team.some(
                    (playerId) =>
                        String(playerId) === String(requestedPlayerId)
                )
            );

            if (requestedTeam) {
                const expansionEntry =
                    getScrambleExpansionEntry(requestedTeam);
                const teamMiniGameWins =
                    getTeamMiniGameWins(requestedTeam);

                if (
                    expansionEntry.hasScorecardData ||
                    teamMiniGameWins > 0
                ) {
                    scorecardEntry = expansionEntry;
                }
            }
        } else {
            scorecardEntry =
                roundScorecardByPlayerId[String(requestedPlayerId)] || null;
        }

        // Vänta tills det publika scorekortet har laddats om enheten ännu
        // inte finns. Effekten körs igen när scorekortsdata blir tillgänglig.
        if (!scorecardEntry) return;

        handledExpansionRequestRef.current = requestId;
        setHasManuallyChangedRound(true);
        setExpandedScorecardId(scorecardEntry.id);

        const scrollTimer = window.setTimeout(() => {
            scorecardRowRefs.current[
                String(requestedPlayerId)
            ]?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }, 450);

        return () => window.clearTimeout(scrollTimer);
        // Funktionen getScrambleExpansionEntry bygger posten från samma
        // beroenden som redan finns nedan.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        expandPlayerRequest?.requestId,
        expandPlayerRequest?.playerId,
        expandPlayerRequest?.roundIndex,
        activeRoundIndex,
        isScramble,
        generatedTeams,
        roundScorecardByPlayerId,
        roundScorecardEntries,
    ]);

    const handlePrev = () => {
        if (activeRoundIndex > 0) {
            setHasManuallyChangedRound(true); // 👈 nytt
            setActiveRoundIndex(activeRoundIndex - 1);
        }
    };

    const handleNext = () => {
        if (activeRoundIndex < rounds.length - 1) {
            setHasManuallyChangedRound(true); // 👈 nytt
            setActiveRoundIndex(activeRoundIndex + 1);
        }
    };

    const getTeamNames = (team) => {
        const names = team.map((id) => players.find((p) => p.id === id)?.name || "Unknown");
        const label = names.join(" & ");

        return (
            <span
                className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[12px] leading-tight sm:text-sm"
                title={label}
            >
                {label}
            </span>
        );
    };



    const miniGames = tournament.miniGames || [];

    const getMiniGameWins = (player, miniGame) =>
        Number(
            player?.miniGameScores?.[String(activeRoundIndex)]?.[miniGame.name]
        ) || 0;

    const getRoundMiniGameWins = (player) =>
        miniGames.reduce(
            (sum, miniGame) => sum + getMiniGameWins(player, miniGame),
            0
        );

    const getRoundMiniGameDeduction = (player) =>
        miniGames.reduce((sum, miniGame) => {
            if (!miniGame.subtract_from_score) return sum;

            return (
                sum +
                getMiniGameWins(player, miniGame) *
                    (Number(miniGame.deduction_value) || 0)
            );
        }, 0);

    const getPlayerRoundHandicap = (player) => {
        const fullHandicap = Number.parseFloat(player?.handicap);

        if (!Number.isFinite(fullHandicap)) return 0;

        return (activeRound.holes ?? 18) === 9
            ? fullHandicap / 2
            : fullHandicap;
    };

    const getIndividualGrossScore = (player) => {
        const netScore = Number(player?.scores?.[activeRoundIndex]);

        if (!isIndividualWithHandicap || !(netScore > 0)) {
            return null;
        }

        // Nettoresultatet sparas som heltal. Bruttot måste därför också
        // återskapas och avrundas till hela slag, precis som i EditScores.
        return Math.round(netScore + getPlayerRoundHandicap(player));
    };

    const getTeamMiniGameWins = (team, miniGame = null) =>
        (team || []).reduce((total, playerId) => {
            const player = (tournament.playerData || []).find(
                (item) => String(item.id) === String(playerId)
            );

            return (
                total +
                (miniGame
                    ? getMiniGameWins(player, miniGame)
                    : getRoundMiniGameWins(player))
            );
        }, 0);

    const renderCombinedMiniGameResult = (wins) => {
        const count = Number(wins) || 0;

        if (count <= 0) {
            return <span className={`${theme.textSecondary} opacity-40`}>–</span>;
        }

        return (
            <span
                className={`${theme.accent} inline-flex w-full items-center justify-center whitespace-nowrap text-center font-semibold tracking-[0.08em]`}
                title={`${count} sidotävlings-vinst${count === 1 ? "" : "er"}`}
                aria-label={`${count} sidotävlings-vinst${count === 1 ? "" : "er"}`}
            >
                <span aria-hidden="true">{"★".repeat(count)}</span>
            </span>
        );
    };

    const renderDesktopMiniGameResult = (wins) => {
        const count = Number(wins) || 0;

        if (count <= 0) {
            return <span className={`${theme.textSecondary} opacity-40`}>–</span>;
        }

        return (
            <span
                className={`${theme.accent} inline-flex items-center justify-center whitespace-nowrap font-semibold tracking-[0.08em]`}
                title={`${count} sidotävlings-vinst${count === 1 ? "" : "er"}`}
                aria-label={`${count} sidotävlings-vinst${count === 1 ? "" : "er"}`}
            >
                <span aria-hidden="true">{"★".repeat(count)}</span>
            </span>
        );
    };

    const getCompactMiniGameName = (miniGame) => {
        const gameType = String(
            miniGame?.game_type || miniGame?.gameType || ""
        ).toLowerCase();
        const name = String(miniGame?.name || "").trim();
        const normalizedName = name.toLowerCase();

        if (
            gameType === "closest_to_pin" ||
            normalizedName === "ctp" ||
            normalizedName.includes("closest to pin")
        ) {
            return "CTP";
        }

        if (
            gameType === "longest_drive" ||
            normalizedName.includes("longest drive")
        ) {
            return "Longest drive";
        }

        return name || "Sidospel";
    };

    const mobileMiniGameLabel = miniGames
        .map(getCompactMiniGameName)
        .filter(Boolean)
        .join(" · ");

    const hasMiniGames = miniGames.length > 0;
    // På mobil visas mini game-vinster i en gemensam kompakt kolumn.
    // Rubriken visar vilka sidotävlingar som ingår och klipps om fler
    // sidospel läggs till än vad mobilbredden rymmer.
    const showMobileMiniGameColumn = hasMiniGames;

    // Mobil- och datorvyn har olika antal synliga kolumner.
    // Expanderingsraden måste därför ha ett eget korrekt colSpan per vy,
    // annars skapar webbläsaren osynliga extrakolumner och huvudtabellen hoppar i sidled.
    const hasHandicapColumn =
        isScrambleWithHandicap || isIndividualWithHandicap;
    const mobileResultColumnCount =
        3 + (showMobileMiniGameColumn ? 1 : 0) + (hasHandicapColumn ? 1 : 0);
    const desktopResultColumnCount =
        3 + miniGames.length + (hasHandicapColumn ? 1 : 0);

    const toggleExpandedScorecard = (entry) => {
        if (!entry) return;
        setExpandedScorecardId((current) =>
            current === entry.id ? null : entry.id
        );
    };

    const handleExpandableRowKeyDown = (event, entry) => {
        if (!entry || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        toggleExpandedScorecard(entry);
    };

    const renderIntegratedScorecardRow = (entry) => {
        if (!entry || expandedScorecardId !== entry.id) return null;

        const scorecardSegments = [
            {
                key: "front",
                label: entry.holeScores.length > 9 ? "Front 9" : "Hål 1–9",
                start: 0,
                end: Math.min(9, entry.holeScores.length),
            },
            ...(entry.holeScores.length > 9
                ? [{ key: "back", label: "Back 9", start: 9, end: entry.holeScores.length }]
                : []),
        ];

        const entryPlayers = (entry.playerIds || [])
            .map((playerId) =>
                (tournament.playerData || []).find(
                    (player) => String(player.id) === String(playerId)
                )
            )
            .filter(Boolean);

        const miniGameWinnersByPlayer = entryPlayers
            .map((player) => {
                const results = miniGames.flatMap((miniGame) => {
                    const wins = getMiniGameWins(player, miniGame);
                    if (wins <= 0) return [];

                    const deductionPerWin = miniGame.subtract_from_score
                        ? Number(miniGame.deduction_value) || 0
                        : 0;

                    return [{
                        key: `${player.id}-${miniGame.id || miniGame.name}`,
                        miniGameName: miniGame.name,
                        wins,
                        deduction: wins * deductionPerWin,
                    }];
                });

                if (results.length === 0) return null;

                return {
                    playerId: player.id,
                    playerName: player.name,
                    results,
                };
            })
            .filter(Boolean);

        const totalHoles = Number(activeRound.holes ?? entry.holeScores.length ?? 18);
        const isScorecardComplete =
            entry.holesPlayed > 0 && entry.holesPlayed >= totalHoles;
        const primaryEntryPlayer = entryPlayers[0] || null;
        const entryTeamScore =
            entry.unitType === "team"
                ? teamScores.find(
                    (teamResult) =>
                        getPlayerIdsKey(teamResult.team) === entry.playerIdsKey
                )?.score
                : null;
        const storedRoundScore =
            entry.unitType === "team"
                ? Number(entryTeamScore)
                : Number(primaryEntryPlayer?.scores?.[activeRoundIndex]);
        const firstPlayerId = entry.playerIds?.[0];
        const secondPlayerId = entry.playerIds?.[1];
        const scrambleHandicap =
            firstPlayerId != null && secondPlayerId != null
                ? teamHandicaps[`${firstPlayerId}-${secondPlayerId}`] ??
                  teamHandicaps[`${secondPlayerId}-${firstPlayerId}`] ??
                  null
                : null;
        const handicapValue =
            entry.unitType === "team"
                ? Number(scrambleHandicap)
                : getPlayerRoundHandicap(primaryEntryPlayer);
        const usesHandicap =
            entry.unitType === "team"
                ? isScrambleWithHandicap
                : isIndividualWithHandicap;
        const grossResult = entry.holesPlayed > 0 ? Number(entry.gross) : null;
        const calculatedNetResult =
            usesHandicap && grossResult != null && Number.isFinite(handicapValue)
                ? Math.max(0, Math.round(grossResult - handicapValue))
                : null;
        const netResult =
            usesHandicap && storedRoundScore > 0
                ? storedRoundScore
                : usesHandicap && isScorecardComplete
                    ? calculatedNetResult
                    : null;
        const appliedHandicap =
            usesHandicap && grossResult != null && netResult != null
                ? Math.max(0, Math.round(grossResult - netResult))
                : null;
        const displayMode =
            isScrambleWithHandicap && entry.unitType === "team"
                ? `${mode} · HCP-justerat`
                : mode;
        const statusLabel =
            roundScorecardData.liveRound?.status === "active"
                ? "Live"
                : roundScorecardData.liveRound?.status
                    ? "Avslutad"
                    : null;

        const scorecardContent = (
            <div className="px-2 py-4 sm:px-5">
                        {entry.hasScorecardData && (
                            <div className="mb-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.18em]`}>
                                            Fullständigt scorekort
                                        </p>
                                        <h5 className={`${theme.heading} mt-1 truncate text-sm font-semibold`}>
                                            {entry.label}
                                        </h5>
                                    </div>

                                    {statusLabel && (
                                        <span className={`${theme.accentSoft} ${theme.accent} inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[9px] font-semibold`}>
                                            <span
                                                aria-hidden="true"
                                                className={`h-1.5 w-1.5 rounded-full ${
                                                    statusLabel === "Live"
                                                        ? "bg-current animate-pulse"
                                                        : "bg-current opacity-60"
                                                }`}
                                            />
                                            {statusLabel}
                                        </span>
                                    )}
                                </div>

                                <div className={`${theme.border} mt-3 border-y py-3`}>
                                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                                        <div>
                                            <p className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.16em]`}>
                                                Spelform
                                            </p>
                                            <p className={`${theme.textPrimary} mt-0.5 text-[11px] font-semibold sm:text-xs`}>
                                                {displayMode}
                                            </p>
                                        </div>

                                        {!isScorecardComplete && entry.holesPlayed > 0 && (
                                            <p className={`${theme.textSecondary} text-[9px] font-medium`}>
                                                {entry.holesPlayed} av {totalHoles} hål registrerade
                                            </p>
                                        )}
                                    </div>

                                    {usesHandicap ? (
                                        <div className="mt-3 grid grid-cols-3 divide-x divide-black/10 text-center">
                                            <div className="px-2 first:pl-0">
                                                <p className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.14em]`}>
                                                    Brutto
                                                </p>
                                                <p className={`${theme.textPrimary} mt-1 text-lg font-semibold tabular-nums sm:text-xl`}>
                                                    {grossResult ?? "–"}
                                                </p>
                                                {roundScorecardPars && grossResult != null && (
                                                    <p className={`${theme.textSecondary} mt-0.5 text-[8px] font-medium tabular-nums`}>
                                                        {formatRelativeToPar(entry.relativeToPar)} mot par
                                                    </p>
                                                )}
                                            </div>

                                            <div className="px-2">
                                                <p className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.14em]`}>
                                                    HCP-avdrag
                                                </p>
                                                <p className={`${theme.textSecondary} mt-1 text-lg font-semibold tabular-nums sm:text-xl`}>
                                                    {appliedHandicap != null
                                                        ? `−${appliedHandicap}`
                                                        : "–"}
                                                </p>
                                            </div>

                                            <div className="px-2 last:pr-0">
                                                <p className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.14em]`}>
                                                    Netto
                                                </p>
                                                <p className={`${theme.total} mt-1 text-lg font-bold tabular-nums sm:text-xl`}>
                                                    {netResult ?? "–"}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mt-3 flex items-end justify-between gap-4">
                                            <div>
                                                <p className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.14em]`}>
                                                    Resultat
                                                </p>
                                                <p className={`${theme.total} mt-1 text-xl font-bold tabular-nums`}>
                                                    {grossResult ?? "–"}
                                                </p>
                                            </div>

                                            {roundScorecardPars && grossResult != null && (
                                                <div className="text-right">
                                                    <p className={`${theme.textSecondary} text-[8px] font-semibold uppercase tracking-[0.14em]`}>
                                                        Mot par
                                                    </p>
                                                    <p className={`${theme.textPrimary} mt-1 text-sm font-semibold tabular-nums`}>
                                                        {formatRelativeToPar(entry.relativeToPar)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {entry.hasScorecardData && (
                            <div className="space-y-3">
                                {scorecardSegments.map((segment) => {
                                const segmentScores = entry.holeScores.slice(
                                    segment.start,
                                    segment.end
                                );
                                const segmentPars = roundScorecardPars?.slice(
                                    segment.start,
                                    segment.end
                                );
                                const segmentGross = segmentScores.reduce(
                                    (sum, score) => sum + Number(score || 0),
                                    0
                                );
                                const played = segmentScores.filter(
                                    (score) => score != null
                                ).length;

                                return (
                                    <div key={segment.key}>
                                        <div className="mb-1.5 flex items-center justify-between px-0.5">
                                            <span className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.16em]`}>
                                                {segment.label}
                                            </span>
                                            {played > 0 && (
                                                <span className={`${theme.textSecondary} text-[9px] font-medium tabular-nums`}>
                                                    {segmentGross} slag
                                                </span>
                                            )}
                                        </div>

                                        <div className={`${theme.border} overflow-hidden rounded-lg border`}>
                                            <table className="w-full table-fixed border-collapse text-[9px] sm:text-[10px]">
                                                <colgroup>
                                                    <col style={{ width: "34px" }} />
                                                    {segmentScores.map((_, index) => (
                                                        <col key={index} />
                                                    ))}
                                                </colgroup>
                                                <thead>
                                                    <tr className={`${theme.primary} ${theme.textOnPrimary}`}>
                                                        <th className="px-0.5 py-2 text-center text-[8px] font-semibold uppercase tracking-[0.08em]">
                                                            Hål
                                                        </th>
                                                        {segmentScores.map((_, index) => (
                                                            <th
                                                                key={segment.start + index + 1}
                                                                className="border-l border-white/10 px-0.5 py-2 text-center font-semibold"
                                                            >
                                                                {segment.start + index + 1}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {segmentPars && (
                                                        <tr className={`${theme.sectionMuted} ${theme.border} border-b`}>
                                                            <th className={`${theme.textSecondary} px-0.5 py-1.5 text-center text-[8px] font-semibold uppercase tracking-[0.08em]`}>
                                                                Par
                                                            </th>
                                                            {segmentPars.map((par, index) => (
                                                                <td
                                                                    key={index}
                                                                    className={`${theme.border} ${theme.textSecondary} border-l px-0.5 py-1.5 text-center font-medium tabular-nums`}
                                                                >
                                                                    {par}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    )}
                                                    <tr className={theme.section}>
                                                        <th className={`${theme.textSecondary} px-0.5 py-2 text-center text-[8px] font-semibold uppercase tracking-[0.08em]`}>
                                                            Slag
                                                        </th>
                                                        {segmentScores.map((score, index) => {
                                                            const absoluteIndex = segment.start + index;
                                                            const par = roundScorecardPars?.[absoluteIndex];

                                                            return (
                                                                <td
                                                                    key={absoluteIndex}
                                                                    className={`${theme.border} border-l px-0.5 py-2 text-center`}
                                                                    title={getHoleResultLabel(score, par)}
                                                                >
                                                                    <span
                                                                        className={`inline-flex h-5 min-w-5 items-center justify-center px-0.5 text-[10px] font-semibold tabular-nums ${getHoleScoreMarkerClass(score, par)}`}
                                                                    >
                                                                        {score ?? "–"}
                                                                    </span>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                                })}
                            </div>
                        )}

                        {miniGameWinnersByPlayer.length > 0 && (
                            <div
                                className={
                                    entry.hasScorecardData
                                        ? `${theme.border} mt-3 border-t pt-3`
                                        : ""
                                }
                            >
                                <p className={`${theme.textSecondary} text-[9px] font-semibold uppercase tracking-[0.16em]`}>
                                    {entry.unitType === "team"
                                        ? "Vinnare av sidotävlingar i laget"
                                        : "Sidotävlingar"}
                                </p>

                                <div className="mt-2 space-y-2.5">
                                    {miniGameWinnersByPlayer.map((winner) => (
                                        <div key={winner.playerId}>
                                            {entry.unitType === "team" && (
                                                <p className={`${theme.textPrimary} text-[11px] font-semibold`}>
                                                    {winner.playerName}
                                                </p>
                                            )}

                                            <div
                                                className={`${
                                                    entry.unitType === "team" ? "mt-1" : ""
                                                } flex flex-wrap gap-x-3 gap-y-1`}
                                            >
                                                {winner.results.map((result) => (
                                                    <span
                                                        key={result.key}
                                                        className="inline-flex items-center gap-1.5 text-[10px]"
                                                    >
                                                        <span
                                                            className={`${theme.accent} font-semibold tracking-[0.08em]`}
                                                            aria-hidden="true"
                                                        >
                                                            {"★".repeat(Math.max(1, Number(result.wins) || 1))}
                                                        </span>
                                                        <span className={theme.textSecondary}>
                                                            {result.miniGameName}
                                                        </span>
                                                        {result.deduction > 0 && (
                                                            <span className={`${theme.positive} font-semibold`}>
                                                                −{result.deduction} slag
                                                            </span>
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
            </div>
        );

        return (
            <Fragment>
                <tr className={`${theme.section} ${theme.border} border-t sm:hidden`}>
                    <td
                        colSpan={mobileResultColumnCount}
                        className="w-full max-w-0 p-0 align-top"
                    >
                        {scorecardContent}
                    </td>
                </tr>
                <tr className={`${theme.section} ${theme.border} hidden border-t sm:table-row`}>
                    <td
                        colSpan={desktopResultColumnCount}
                        className="w-full max-w-0 p-0 align-top"
                    >
                        {scorecardContent}
                    </td>
                </tr>
            </Fragment>
        );
    };


    return (
        <section className={`${theme.section} ${theme.border} ${theme.textPrimary} overflow-hidden rounded-2xl border shadow-sm`}>
            {/* Round header */}
            <div className={`${theme.primary} ${theme.textOnPrimary} px-4 py-5 sm:px-7 sm:py-6`}>
                <div className="flex items-center gap-3 sm:gap-5">
                    <button
                        type="button"
                        onClick={handlePrev}
                        disabled={activeRoundIndex === 0}
                        aria-label="Föregående runda"
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-xl transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-25"
                    >
                        ←
                    </button>

                    <div className="min-w-0 flex-1 text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/70 sm:text-[11px]">
                            Runda {activeRoundIndex + 1} av {rounds.length}
                        </p>
                        <h3 className="mt-1 truncate text-lg font-semibold tracking-tight sm:text-2xl">
                            {activeRound.name}
                        </h3>
                    </div>

                    <button
                        type="button"
                        onClick={handleNext}
                        disabled={activeRoundIndex === rounds.length - 1}
                        aria-label="Nästa runda"
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-xl transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-25"
                    >
                        →
                    </button>
                </div>

                <div className="mt-4 flex flex-wrap justify-center gap-2 text-[11px] font-medium sm:text-xs">
                    <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5">
                        {mode}
                    </span>
                    <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5">
                        {activeRound.holes ?? 18} hål
                    </span>
                    {activeRound.scrambleOptions?.scrambleWithHandicap && (
                        <span className={`${theme.accentSoft} ${theme.accent} ${theme.borderStrong} rounded-full border px-3 py-1.5`}>
                            HCP-justerat
                        </span>
                    )}
                </div>

                {isScramble && (
                    <p className="mt-3 text-center text-xs text-white/70">
                        Lagindelning: {activeRound.scrambleOptions?.pairing === "custom"
                            ? "Egen indelning"
                            : activeRound.scrambleOptions?.pairing === "position"
                                ? "Position"
                                : activeRound.scrambleOptions?.pairing === "handicap"
                                    ? "Handicap"
                                    : activeRound.scrambleOptions?.pairing || "Ej angiven"}
                        {tournament.team_mode ? " · inom ordinarie lag" : ""}
                    </p>
                )}
            </div>

            {/* Round results */}
            <div className="px-4 py-5 sm:px-7 sm:py-7">
                <div className="mb-4 flex items-end justify-between gap-4">
                    <div>
                        <p className={`${theme.textSecondary} text-[10px] font-semibold uppercase tracking-[0.24em]`}>
                            Rondresultat
                        </p>
                        <h4 className={`${theme.heading} mt-1 text-xl font-semibold tracking-tight`}>
                            {isScramble ? "Lagresultat" : "Spelarresultat"}
                        </h4>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        <span className={`${theme.section} ${theme.border} ${theme.textSecondary} rounded-full border px-3 py-1.5 text-xs font-medium`}>
                            {isScramble ? teamScores.length : tournament.playerData?.length || 0} {isScramble ? "lag" : "spelare"}
                        </span>
                        {roundScorecardData.loading && (
                            <span className={`${theme.sectionMuted} ${theme.border} ${theme.textSecondary} rounded-full border px-3 py-1.5 text-xs font-medium`}>
                                Hämtar scorekort…
                            </span>
                        )}
                    </div>
                </div>

                <div className={`${theme.section} ${theme.border} -mx-2 overflow-hidden rounded-xl border sm:mx-0`}>
                    <table className="w-full table-fixed text-left text-xs sm:text-sm">
                        <colgroup>
                            <col />
                            {showMobileMiniGameColumn && <col className="w-[112px] sm:hidden" />}
                            {miniGames.map((miniGame) => (
                                <col
                                    key={miniGame.id || miniGame.name}
                                    className="hidden w-[88px] sm:table-column"
                                />
                            ))}
                            {(isScrambleWithHandicap || isIndividualWithHandicap) && (
                                <col className="w-[48px] sm:w-[88px]" />
                            )}
                            <col className="w-[50px] sm:w-[88px]" />
                            <col className="w-[28px] sm:w-[38px]" />
                        </colgroup>
                        <thead className={`${theme.sectionMuted} ${theme.border} ${theme.textSecondary} border-b text-[9px] font-semibold uppercase tracking-[0.12em] sm:text-[11px]`}>
                            <tr>
                                <th className="px-2 py-3 sm:px-5">
                                    {isScramble ? "Lag" : "Spelare"}
                                </th>

                                {showMobileMiniGameColumn && (
                                    <th
                                        className="min-w-0 px-1.5 py-3 text-center normal-case tracking-normal sm:hidden"
                                        title={mobileMiniGameLabel}
                                        aria-label={`Sidotävlingar: ${mobileMiniGameLabel}`}
                                    >
                                        <span className="mx-auto block max-w-[104px] truncate whitespace-nowrap text-center text-[9px] font-semibold leading-tight">
                                            {mobileMiniGameLabel}
                                        </span>
                                    </th>
                                )}

                                {miniGames.map((miniGame) => (
                                    <th
                                        key={miniGame.id || miniGame.name}
                                        className="hidden px-1.5 py-3 text-center normal-case tracking-normal sm:table-cell"
                                    >
                                        <span className="block break-words text-[10px] font-semibold leading-tight">
                                            {miniGame.name}
                                        </span>
                                    </th>
                                ))}

                                {(isScrambleWithHandicap || isIndividualWithHandicap) && (
                                    <th className="px-1 py-3 text-center">
                                        Brutto
                                    </th>
                                )}

                                <th
                                    className={`${theme.total} px-1 py-3 text-center`}
                                    title={
                                        isIndividualWithHandicap
                                            ? "Netto före eventuellt stjärnavdrag"
                                            : isScrambleWithHandicap
                                                ? "HCP-justerat lagresultat"
                                                : undefined
                                    }
                                >
                                    {isIndividualWithHandicap || isScrambleWithHandicap
                                        ? "Netto"
                                        : "Total"}
                                </th>

                                <th className="p-0 text-center">
                                    <span className="sr-only">Visa scorekort</span>
                                </th>
                            </tr>
                        </thead>

                        <tbody className={`divide-y ${theme.border}`}>
                            {isScramble
                                ? [...teamScores]
                                    .sort(
                                        (a, b) =>
                                            (a.score > 0 ? a.score : Infinity) -
                                            (b.score > 0 ? b.score : Infinity)
                                    )
                                    .map((t, i) => {
                                        const teamMiniGameWins = getTeamMiniGameWins(t.team);
                                        const expansionEntry =
                                            getScrambleExpansionEntry(t.team);
                                        const scorecardEntry =
                                            expansionEntry.hasScorecardData ||
                                            teamMiniGameWins > 0
                                                ? expansionEntry
                                                : null;
                                        const isExpanded =
                                            scorecardEntry &&
                                            expandedScorecardId === scorecardEntry.id;

                                        return (
                                            <Fragment key={getPlayerIdsKey(t.team) || i}>
                                                <tr
                                                    ref={(node) => {
                                                        t.team.forEach((playerId) => {
                                                            const key = String(playerId);
                                                            if (node) {
                                                                scorecardRowRefs.current[key] = node;
                                                            } else {
                                                                delete scorecardRowRefs.current[key];
                                                            }
                                                        });
                                                    }}
                                                    onClick={() => toggleExpandedScorecard(scorecardEntry)}
                                                    onKeyDown={(event) =>
                                                        handleExpandableRowKeyDown(event, scorecardEntry)
                                                    }
                                                    tabIndex={scorecardEntry ? 0 : undefined}
                                                    role={scorecardEntry ? "button" : undefined}
                                                    aria-expanded={scorecardEntry ? isExpanded : undefined}
                                                    className={`group ${i % 2 === 0 ? theme.tableRow : theme.tableRowAlternate} ${scorecardEntry ? "cursor-pointer hover:bg-black/[0.025]" : ""} transition`}
                                                >
                                                    <td className="min-w-0 px-2 py-3 sm:px-5 sm:py-3.5">
                                                        <div className="flex min-w-0 items-start gap-2">
                                                            <span className={`${theme.primarySoft} ${theme.heading} flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold sm:h-7 sm:w-7 sm:text-xs`}>
                                                                {i + 1}
                                                            </span>
                                                            <div className="min-w-0 flex-1">
                                                                <span className={`${theme.textPrimary} block min-w-0 font-medium`}>
                                                                    {getTeamNames(t.team)}
                                                                </span>
                                                                {isScrambleWithHandicap && (
                                                                    <span className={`${theme.textSecondary} mt-0.5 block truncate text-[9px] font-medium leading-tight sm:text-[10px]`}>
                                                                        Lag-HCP {teamHandicaps[`${t.team[0]}-${t.team[1]}`] ?? "–"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {showMobileMiniGameColumn && (
                                                        <td className="px-1.5 py-3 text-center align-middle sm:hidden">
                                                            {renderCombinedMiniGameResult(teamMiniGameWins)}
                                                        </td>
                                                    )}

                                                    {miniGames.map((miniGame) => (
                                                        <td
                                                            key={miniGame.id || miniGame.name}
                                                            className="hidden px-1.5 py-3 text-center sm:table-cell"
                                                        >
                                                            {renderDesktopMiniGameResult(
                                                                getTeamMiniGameWins(t.team, miniGame)
                                                            )}
                                                        </td>
                                                    ))}

                                                    {isScrambleWithHandicap && (
                                                        <td className={`${theme.textSecondary} px-1 py-3 text-center tabular-nums`}>
                                                            {(() => {
                                                                const rawScore = t.score ?? 0;
                                                                if (rawScore === 0) return "–";

                                                                const teamHcp =
                                                                    teamHandicaps[
                                                                        `${t.team[0]}-${t.team[1]}`
                                                                    ] ?? 0;
                                                                const isNineHoles = activeRound?.holes === 9;
                                                                const adjustedHcp = isNineHoles
                                                                    ? teamHcp / 2
                                                                    : teamHcp;

                                                                return Math.round(rawScore + adjustedHcp);
                                                            })()}
                                                        </td>
                                                    )}

                                                    <td className={`${theme.total} px-1 py-3 text-center text-sm font-semibold tabular-nums sm:py-3.5 sm:text-base`}>
                                                        {t.score > 0 ? t.score : "–"}
                                                    </td>

                                                    <td className="p-0 text-center align-middle">
                                                        {scorecardEntry && (
                                                            <span
                                                                aria-hidden="true"
                                                                className={`${theme.accent} mx-auto inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors group-hover:bg-black/[0.035]`}
                                                            >
                                                                <ChevronDownIcon
                                                                    className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                                                />
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {renderIntegratedScorecardRow(scorecardEntry)}
                                            </Fragment>
                                        );
                                    })
                                : [...(tournament.playerData || [])]
                                    .sort((a, b) => {
                                        const aScore = a.scores?.[activeRoundIndex] > 0
                                            ? a.scores[activeRoundIndex]
                                            : Infinity;
                                        const bScore = b.scores?.[activeRoundIndex] > 0
                                            ? b.scores[activeRoundIndex]
                                            : Infinity;
                                        return aScore - bScore;
                                    })
                                    .map((player, i) => {
                                        const scorecardEntry =
                                            roundScorecardByPlayerId[String(player.id)];
                                        const isExpanded =
                                            scorecardEntry &&
                                            expandedScorecardId === scorecardEntry.id;
                                        const miniGameWins = getRoundMiniGameWins(player);
                                        const miniGameDeduction =
                                            getRoundMiniGameDeduction(player);
                                        const grossScore =
                                            getIndividualGrossScore(player);

                                        return (
                                            <Fragment key={player.id}>
                                                <tr
                                                    ref={(node) => {
                                                        const key = String(player.id);
                                                        if (node) {
                                                            scorecardRowRefs.current[key] = node;
                                                        } else {
                                                            delete scorecardRowRefs.current[key];
                                                        }
                                                    }}
                                                    onClick={() => toggleExpandedScorecard(scorecardEntry)}
                                                    onKeyDown={(event) =>
                                                        handleExpandableRowKeyDown(event, scorecardEntry)
                                                    }
                                                    tabIndex={scorecardEntry ? 0 : undefined}
                                                    role={scorecardEntry ? "button" : undefined}
                                                    aria-expanded={scorecardEntry ? isExpanded : undefined}
                                                    className={`group ${i % 2 === 0 ? theme.tableRow : theme.tableRowAlternate} ${scorecardEntry ? "cursor-pointer hover:bg-black/[0.025]" : ""} transition`}
                                                >
                                                    <td className="min-w-0 px-2 py-3 sm:px-5 sm:py-3.5">
                                                        <div className="flex min-w-0 items-center gap-2">
                                                            <span className={`${theme.primarySoft} ${theme.heading} flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold sm:h-7 sm:w-7 sm:text-xs`}>
                                                                {i + 1}
                                                            </span>
                                                            <span className={`${theme.textPrimary} min-w-0 flex-1 truncate font-medium`}>
                                                                {player.name}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    {showMobileMiniGameColumn && (
                                                        <td className="px-1.5 py-3 text-center align-middle sm:hidden">
                                                            {renderCombinedMiniGameResult(miniGameWins)}
                                                        </td>
                                                    )}

                                                    {miniGames.map((miniGame) => (
                                                        <td
                                                            key={miniGame.id || miniGame.name}
                                                            className="hidden px-1.5 py-3 text-center sm:table-cell"
                                                        >
                                                            {renderDesktopMiniGameResult(
                                                                getMiniGameWins(player, miniGame)
                                                            )}
                                                        </td>
                                                    ))}

                                                    {isIndividualWithHandicap && (
                                                        <td className={`${theme.textSecondary} px-1 py-3 text-center tabular-nums`}>
                                                            {grossScore ?? "–"}
                                                        </td>
                                                    )}

                                                    <td
                                                        className={`${theme.total} px-1 py-2.5 text-center tabular-nums sm:py-3`}
                                                    >
                                                        <div className="flex flex-col items-center justify-center leading-none">
                                                            <span className="text-sm font-semibold sm:text-base">
                                                                {player.scores?.[activeRoundIndex] || "–"}
                                                            </span>
                                                            {miniGameDeduction > 0 && (
                                                                <span
                                                                    className={`${theme.positive} mt-1 whitespace-nowrap text-[8px] font-semibold leading-none sm:text-[9px]`}
                                                                    title={`Stjärnavdrag −${miniGameDeduction} slag. Avdraget räknas i totalställningen, inte i denna rundvy.`}
                                                                    aria-label={`Stjärnavdrag minus ${miniGameDeduction} slag`}
                                                                >
                                                                    (−{miniGameDeduction})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="p-0 text-center align-middle">
                                                        {scorecardEntry && (
                                                            <span
                                                                aria-hidden="true"
                                                                className={`${theme.accent} mx-auto inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors group-hover:bg-black/[0.035]`}
                                                            >
                                                                <ChevronDownIcon
                                                                    className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                                                />
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {renderIntegratedScorecardRow(scorecardEntry)}
                                            </Fragment>
                                        );
                                    })}
                        </tbody>
                    </table>
                </div>

                {/* Mini game-detaljer visas i den expanderade raden ovan. */}

                {/* Round information */}
                {activeRound.info?.trim() && (
                    <div className={`mt-7 border-t ${theme.border} pt-5`}>
                        <button
                            type="button"
                            onClick={() => setShowInfo(!showInfo)}
                            className={`${theme.section} ${theme.border} ${theme.heading} flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-semibold transition`}
                        >
                            <span>{showInfo ? "Dölj rondinformation" : "Visa rondinformation"}</span>
                            <span
                                aria-hidden="true"
                                className={`text-lg transition-transform duration-200 ${showInfo ? "rotate-180" : ""}`}
                            >
                                ⌄
                            </span>
                        </button>

                        <AnimatePresence initial={false}>
                            {showInfo && (
                                <motion.div
                                    key="info-box"
                                    initial={{ opacity: 0, y: -8, height: 0 }}
                                    animate={{ opacity: 1, y: 0, height: "auto" }}
                                    exit={{ opacity: 0, y: -8, height: 0 }}
                                    transition={{ duration: 0.25 }}
                                    className="overflow-hidden"
                                >
                                    <div className={`${theme.accentSoft} ${theme.borderStrong} mt-3 rounded-xl border border-l-4 px-5 py-5 sm:px-6`}>
                                        <p className={`${theme.textSecondary} mb-3 text-[10px] font-semibold uppercase tracking-[0.22em]`}>
                                            Rondinformation
                                        </p>
                                        <div
                                            className={`${theme.textPrimary} text-sm leading-7`}
                                            dangerouslySetInnerHTML={{
                                                __html: activeRound.info.replace(/\n/g, "<br />"),
                                            }}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </section>
    );
}